import type {RefObject} from 'react';
import {useCallback, useEffect} from 'react';

/** On touch devices, focusing the chat prompt shows the newest message and keeps it in view while the on-screen keyboard shrinks the viewport. */
export function useScrollChatToBottomOnPromptFocus(promptBoxRef: RefObject<HTMLDivElement | null>, scrollChatToBottom: VoidFunction): VoidFunction {
  useEffect(() => {
    const {visualViewport} = window;
    if (!visualViewport || !window.PMW.isMobileOS()) {
      return undefined;
    }

    let previousViewportHeight = getUnzoomedHeight(visualViewport);
    const scrollToBottomWhenKeyboardOpens = (): void => {
      const viewportHeight = getUnzoomedHeight(visualViewport);
      const didViewportShrink = viewportHeight < previousViewportHeight;
      previousViewportHeight = viewportHeight;

      if (didViewportShrink && isPromptFocused(promptBoxRef.current)) {
        scrollChatToBottom();
      }
    };

    visualViewport.addEventListener('resize', scrollToBottomWhenKeyboardOpens);

    return (): void => {
      visualViewport.removeEventListener('resize', scrollToBottomWhenKeyboardOpens);
    };
  }, [promptBoxRef, scrollChatToBottom]);

  return useCallback((): void => {
    if (window.PMW.isMobileOS()) {
      scrollChatToBottom();
    }
  }, [scrollChatToBottom]);
}

/** Pinch-zoom shrinks the visual viewport too; scaling it back out leaves only the keyboard's shrink. */
const getUnzoomedHeight = (visualViewport: VisualViewport): number => {
  return visualViewport.height * visualViewport.scale;
};

const isPromptFocused = (promptBox: HTMLDivElement | null): boolean => {
  return promptBox?.contains(document.activeElement) ?? false;
};
